import { toBillMode } from '@bbpos/ui/src/components/POSBillingComponents/BillModeSelector/types';
import { ApiError } from '@/lib/api-client';
import { diffDraftLines } from '@/lib/draft-line-diff';
import { billingService, type CreateBillItemDto } from '@/services/billing.service';
import { vouchersService } from '@/services/vouchers.service';
import { offlineBillingService } from '@/services/offline-billing.service';
import type { DraftBill, DraftBillItem } from '@/db/pos-database';

/**
 * gst-parked 120 (owner decisions D1 / D2) — parked bills live on the server
 * and belong to the store: every till there lists them and any cashier may
 * resume one. Parking with no connection keeps the bill in Dexie
 * (`draft_bills`, `is_parked = 1`); it is uploaded — draft, then park — when
 * the till is back online, before logout and before a key-rotation wipe.
 */

/** One cart line as parking reads it. */
export interface ParkableLine {
  productId: string;
  quantity: number;
  unitPrice: number;
  /** Catalogue price before add-ons; the difference travels as a surcharge. */
  listPrice?: number;
  /** Per-line discount in minor units, as the cart computed it. */
  discountAmount: number;
}

export interface ParkableCart {
  /** The cart's client id — the create's idempotency key and `bills.client_id`. */
  clientId: string;
  /** The server DRAFT the cart already has (resumed, or a park's earlier attempt). */
  serverBillId: string | null;
  locationId: string;
  items: ParkableLine[];
  customerId: string | null;
  billDiscount: {
    type: 'PERCENTAGE' | 'FLAT';
    /** Whole percent for PERCENTAGE, minor units for FLAT. */
    value: number;
    reason: string;
    voucherCode?: string;
  } | null;
  /** The bill discount is already on the server bill (resumed or manager-approved). */
  billDiscountOnServer: boolean;
  /** The label every till's list shows, e.g. "Apple Watch SE +2 more". */
  note?: string;
}

export interface ServerParkResult {
  billId: string;
  /** PKD-YYYYMMDD-NNNN. */
  parkReference: string | null;
}

/**
 * The bill can't be parked on the server whatever is retried (completed or
 * voided meanwhile). An upload keeps the reason on the till's row.
 */
export class ParkRefusedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ParkRefusedError';
  }
}

/** `BillDiscount.appliedBy` markers of a discount the server already holds (use-cart-store). */
const DISCOUNT_ON_SERVER_MARKERS = new Set(['resume', 'approval']);

export function discountIsOnServer(appliedBy: string | undefined | null): boolean {
  return !!appliedBy && DISCOUNT_ON_SERVER_MARKERS.has(appliedBy);
}

/**
 * A park line for POST /bills. No `expected_unit_price`: nobody is charged at
 * park time, so the server prices the bill from the catalogue as it stands and
 * the cashier sees that price on resume, instead of the park failing on a
 * price change (PRICE_CHANGED) — and an upload has no cashier to answer it.
 */
function toParkItem(line: ParkableLine): CreateBillItemDto {
  const surcharge = line.listPrice !== undefined ? line.unitPrice - line.listPrice : 0;
  return {
    product_id: line.productId,
    quantity: line.quantity,
    ...(surcharge > 0 ? { modifier_surcharge: surcharge } : {}),
    ...(line.discountAmount > 0 ? { discount: line.discountAmount } : {}),
  };
}

/**
 * Park a cart on the server: make sure it has a DRAFT holding its lines and
 * bill discount, then POST /bills/:id/park. Safe to repeat — the create is
 * keyed by the client id, a draft already PARKED counts as done.
 *
 * `onDraftCreated` runs as soon as a new draft exists, so a caller that fails
 * later can keep its id and finish the same bill next time.
 */
export async function parkOnServer(
  cart: ParkableCart,
  onDraftCreated?: (billId: string) => Promise<void> | void,
): Promise<ServerParkResult> {
  let billId = cart.serverBillId;
  let discountOnServer = cart.billDiscountOnServer;

  if (billId) {
    const server = await billingService.getBill(billId);
    const status = server.status as string;
    if (status === 'PARKED') {
      return { billId, parkReference: parkReferenceOf(server) };
    }
    if (status !== 'DRAFT') {
      throw new ParkRefusedError(
        status === 'PENDING_DISCOUNT_APPROVAL'
          ? 'This bill is waiting for a manager to approve its discount.'
          : `Bill ${server.bill_number} was already ${status === 'VOID' ? 'voided' : 'completed'}.`,
      );
    }
    // The same line diff checkout sends: edited lines keep their price.
    const lineDiff = diffDraftLines(server.items, cart.items);
    if (lineDiff) await billingService.updateDraftBill(billId, lineDiff);
    const serverHadBillDiscount =
      !!server.discount_details ||
      Number(server.discount_total ?? 0) -
        server.items.reduce((sum, i) => sum + Number(i.line_discount ?? 0), 0) >
        0;
    if (!cart.billDiscount && serverHadBillDiscount) {
      await billingService.clearDiscount(billId);
    }
    // An earlier attempt already redeemed this voucher on the draft.
    const voucher = cart.billDiscount?.voucherCode;
    if (voucher && server.discount_details?.voucher_code === voucher) discountOnServer = true;
  } else {
    const draft = await billingService.createBill(
      {
        location_id: cart.locationId,
        items: cart.items.map(toParkItem),
        ...(cart.customerId ? { customer_id: cart.customerId } : {}),
        client_id: cart.clientId,
      },
      cart.clientId,
    );
    billId = draft.id;
    discountOnServer = false;
    await onDraftCreated?.(billId);
  }

  const discount = cart.billDiscount;
  if (discount && !discountOnServer) {
    if (discount.voucherCode) {
      await vouchersService.redeem(discount.voucherCode, {
        bill_id: billId,
        ...(cart.customerId ? { customer_id: cart.customerId } : {}),
      });
    } else {
      await billingService.applyDiscount(billId, {
        type: discount.type,
        // The server reads a percentage in basis points.
        value: discount.type === 'PERCENTAGE' ? Math.round(discount.value * 100) : discount.value,
        reason: discount.reason,
      });
    }
  }

  try {
    const parked = await billingService.parkBill(billId, cart.note);
    return { billId, parkReference: parked?.park_reference ?? null };
  } catch (err) {
    // A park whose reply was lost: the retry finds it PARKED already.
    if (err instanceof ApiError && err.statusCode === 409) {
      const now = await billingService.getBill(billId);
      if ((now.status as string) === 'PARKED') return { billId, parkReference: parkReferenceOf(now) };
    }
    throw err;
  }
}

function parkReferenceOf(bill: unknown): string | null {
  return (bill as { park_reference?: string | null }).park_reference ?? null;
}

/**
 * The connection failed (no reply, or the server is down) — park on the till
 * instead. Anything else is the server's answer and goes to the cashier.
 */
export function isOfflineParkError(err: unknown): boolean {
  if (err instanceof ParkRefusedError) return false;
  if (err instanceof ApiError) return err.statusCode >= 500 || err.statusCode === 401;
  return true;
}

/**
 * An upload the server refused for good (validation, product gone, bill
 * settled elsewhere): keep the reason on the row and go on with the next.
 * Busy / locked / rate-limited / signed-out / offline: stop and retry later.
 */
function isPermanentUploadError(err: unknown): boolean {
  if (err instanceof ParkRefusedError) return true;
  if (!(err instanceof ApiError)) return false;
  return err.statusCode >= 400 && err.statusCode < 500 && ![401, 408, 409, 429].includes(err.statusCode);
}

/**
 * A sale cart parked on this till waits for upload; a return / quotation cart
 * stays here, and an empty row holds nothing. A row this key can't read (no
 * `items`) counts as waiting, so it is never wiped unread.
 */
export function isUploadableParkedDraft(draft: DraftBill): boolean {
  return (
    draft.is_parked === 1 &&
    toBillMode(draft.bill_mode) === 'sale' &&
    !draft.pending_discount_approval &&
    (!Array.isArray(draft.items) || draft.items.length > 0)
  );
}

/** The till's per-line discount (minor units) from a stored line. */
function storedLineDiscount(item: DraftBillItem): number {
  const subtotal = Math.round(item.unitPrice * item.quantity);
  if (item.discountType === 'PERCENTAGE') return Math.floor((subtotal * item.discountValue) / 100);
  if (item.discountType === 'FLAT') return Math.min(item.discountValue, subtotal);
  return 0;
}

/** "Apple Watch SE" / "Apple Watch SE +2 more" — the label every till's list shows. */
export function parkNote(items: ReadonlyArray<{ name: string }>): string | undefined {
  if (items.length === 0) return undefined;
  const rest = items.length - 1;
  return rest > 0 ? `${items[0].name} +${rest} more` : items[0].name;
}

function parkableFromDraft(draft: DraftBill): ParkableCart {
  const stored = draft.bill_discount as (DraftBill['bill_discount'] & { voucherCode?: string }) | null | undefined;
  return {
    clientId: draft.client_id,
    serverBillId: draft.server_bill_id ?? null,
    locationId: draft.location_id as string,
    items: draft.items.map((i) => ({
      productId: i.productId,
      quantity: i.quantity,
      unitPrice: i.unitPrice,
      ...(i.listPrice !== undefined ? { listPrice: i.listPrice } : {}),
      discountAmount: storedLineDiscount(i),
    })),
    customerId: draft.customer_id,
    billDiscount: stored
      ? {
          type: stored.type,
          value: stored.value,
          reason: stored.reason,
          ...(stored.voucherCode ? { voucherCode: stored.voucherCode } : {}),
        }
      : null,
    billDiscountOnServer: discountIsOnServer(stored?.appliedBy),
    note: parkNote(draft.items),
  };
}

export interface ParkUploadResult {
  pushed: number;
  /** Parked sale carts still on this till (not yet on the server). */
  remaining: number;
}

let inFlight: Promise<ParkUploadResult> | null = null;

/**
 * Upload every parked sale cart on this till (all stores), oldest first. One
 * run at a time: a second caller shares the run in progress.
 */
export function pushLocalParkedBills(): Promise<ParkUploadResult> {
  if (!inFlight) {
    inFlight = runUpload().finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
}

async function runUpload(): Promise<ParkUploadResult> {
  const rows = (await offlineBillingService.loadParkedDrafts())
    .filter(isUploadableParkedDraft)
    .sort((a, b) => String(a.created_at).localeCompare(String(b.created_at)));
  let pushed = 0;
  for (const row of rows) {
    // A row this key can't read (or an empty one) can't be sent; it stays and
    // keeps counting, so it is never wiped unread.
    if (!row.location_id || !Array.isArray(row.items) || row.items.length === 0) continue;
    const upload = () =>
      parkOnServer(parkableFromDraft(row), (billId) =>
        offlineBillingService.patchParkedDraft(row.client_id, { server_bill_id: billId }),
      );
    try {
      try {
        await upload();
      } catch (err) {
        // The register this till had open closed while the park waited (the
        // create stamps it strictly). createBill has forgotten that session,
        // so the second try goes without it.
        if ((err as { code?: string } | null)?.code !== 'REGISTER_SESSION_NOT_OPEN') throw err;
        await upload();
      }
      await offlineBillingService.removeDraft(row.client_id);
      pushed++;
    } catch (err) {
      if (!isPermanentUploadError(err)) break; // offline again — the next trigger retries
      await offlineBillingService
        .patchParkedDraft(row.client_id, { upload_error: err instanceof Error ? err.message : String(err) })
        .catch(() => undefined);
    }
  }
  return { pushed, remaining: await countWaitingParkedBills() };
}

/** Parked sale carts on this till that are not on the server yet — logout waits for them. */
export async function countWaitingParkedBills(): Promise<number> {
  const rows = await offlineBillingService.loadParkedDrafts();
  return rows.filter(isUploadableParkedDraft).length;
}
